import { BasicButton, InputTextArea, Loading, Typography } from "@/components";
import { BackAppBar } from "@/components/ui";
import useCaseMutation from "@/hooks/useCaseMutation";
import { LETTER_TYPE_VALUE } from "@/libraries/constant";
import { getCaseLetter, getCaseLetterPdfFile } from "@/queries";
import { EmailListSchema, SendCaseLetterFormFieldSchema } from "@/schemas";
import { usePrompt } from "@/stores/prompt";
import { zodResolver } from "@hookform/resolvers/zod";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import InsertDriveFileRoundedIcon from "@mui/icons-material/InsertDriveFileRounded";
import Alert from "@mui/material/Alert";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import omit from "lodash/omit";
import { Controller, type SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

export const Route = createFileRoute(
	"/dashboard/case/view/$id/letter/$letterId/export",
)({
	component: RouteComponent,
});

function RouteComponent() {
	const { t } = useTranslation("dashboard");
	const navigate = Route.useNavigate();
	const caseId = Route.useParams({
		select: (state) => state.id,
	});
	const letterId = Route.useParams({
		select: (s) => s.letterId,
	});
	const {
		data: { case_letter },
	} = useSuspenseQuery(getCaseLetter(letterId));
	const {
		watch,
		control,
		handleSubmit,
		formState: { isValid, isDirty, isSubmitting },
	} = useForm<SendCaseLetterFormFieldSchema>({
		mode: "all",
		resolver: zodResolver(SendCaseLetterFormFieldSchema),
		defaultValues: {
			id: letterId,
			emails: "",
			note: "",
		},
	});

	const watchEmail = watch("emails");
	const parseEmail = EmailListSchema.safeParse(watchEmail);

	const openPrompt = usePrompt((state) => state.open);
	const { sendLetter } = useCaseMutation();
	const casePdf = useSuspenseQuery(getCaseLetterPdfFile(letterId));

	const onSubmit: SubmitHandler<SendCaseLetterFormFieldSchema> = (data, e) => {
		e?.stopPropagation();
		e?.preventDefault();
		return openPrompt({
			title: t("dashboard:case.consentPromptTitle"),
			positiveButton: {
				label: t("dashboard:case.yes"),
				variant: "contained",
				color: "primary",
				onClick: () => {
					if (!parseEmail.success) {
						return toast.error(t("dashboard:case.invalidEmailFormat"));
					}
					return sendLetter.mutateAsync(data, {
						onSuccess: () => {
							navigate({
								to: "/dashboard/case/view/$id/letter/$letterId",
								params: {
									id: caseId,
									letterId,
								},
							});
							return toast.success(t("dashboard:case.letterSentSuccess"));
						},
						onError: () => {
							return toast.error(t("dashboard:case.exportEmailFailed"));
						},
					});
				},
			},
			negativeButton: {
				label: t("dashboard:case.cancel"),
				variant: "outlined",
				color: "primary",
			},
		});
	};

	return (
		<>
			<BackAppBar
				title={t("dashboard:case.sendLetterAsEmail", {
					letterType: LETTER_TYPE_VALUE[case_letter.letter_type],
				})}
				className="pb-3"
			>
				<Alert
					icon={<InfoRoundedIcon className="!fill-primary" />}
					severity="info"
					className="!bg-blue-100"
				>
					<Typography fontSize={14}>
						{t("dashboard:case.sendLetterWarning")}
					</Typography>
				</Alert>
			</BackAppBar>
			<form
				onSubmit={handleSubmit(onSubmit)}
				className="container bg-white flex flex-col flex-1"
			>
				<div className="flex flex-col flex-1 py-4 gap-6">
					<Controller
						control={control}
						name="emails"
						render={({ field }) => (
							<TextField
								id={field.name}
								type="text"
								label={t("dashboard:case.recipientEmails")}
								helperText={t("dashboard:case.recipientEmailsHelper")}
								error={!parseEmail.success}
								className="[&_.MuiFormLabel-asterisk]:text-red-500 [&_.MuiInputBase-root]:!rounded-xl"
								{...omit(field, ["ref"])}
							/>
						)}
					/>
					<Controller
						control={control}
						name="note"
						render={({ field, fieldState }) => (
							<div>
								<InputTextArea
									fullWidth
									rows={4}
									maxRows={4}
									label={t("dashboard:case.noteRecommended")}
									maxLength={200}
									error={!!fieldState.error?.message}
									maxLengthClassName="!bottom-0"
									{...omit(field, ["ref"])}
								/>
								<Typography
									fontWeight={400}
									fontSize={12}
									className="pl-4 text-[#00000099]"
								>
									{t("dashboard:case.noteAccompany", {
										letterType: LETTER_TYPE_VALUE[case_letter.letter_type],
									})}
								</Typography>
							</div>
						)}
					/>
					{casePdf.data ? (
						<div className="w-fit space-y-2">
							<Typography fontSize={14}>
								{t("dashboard:case.attachment")}
							</Typography>
							<div className="w-fit border border-gray-100 rounded-xl">
								<IconButton
									LinkComponent="a"
									target="_blank"
									className="!rounded-xl"
									href={URL.createObjectURL(casePdf.data)}
									title={t("dashboard:case.summaryOfMedicalReport", { caseId })}
								>
									<InsertDriveFileRoundedIcon />
								</IconButton>
							</div>
						</div>
					) : null}
				</div>
				<div className="container py-4 shadow">
					<BasicButton
						type="submit"
						variant="contained"
						color="secondary"
						disabled={!isDirty || !isValid}
						loading={isSubmitting}
						className="w-full !h-12 text-sm font-semibold !shadow-none"
					>
						{t("dashboard:case.submit")}
					</BasicButton>
				</div>
			</form>
			<Loading status={sendLetter.isPending} />
		</>
	);
}
